import { Form, Select } from "antd";
import React, { useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { Button, Upload, Message, Spin } from "../../../BP2Components";
import { useFormContext } from "../../../../context/FormContext/FormContext";
import { handleGridTechname, renderDevInfo } from "../../GeneralScripts";
import SignatureCanvas from "react-signature-canvas";
import "./SignatureField.less";
import projectConfig from "../../../../projectConfig.json";
import { uploadFieldFile } from "../../../../services/restAPI/RestApi";
import { getBlob } from "../../../../services/restAPI/RestApi";
import FieldInfo from "../FieldInfo/FieldInfo";
import { handleTemplateAlert } from "../../../../util/Scripts";

const { hostCloud } = projectConfig.api;

const SignatureField = ({
   isRequired,
   key,
   element,
   form,
   isVisible,
   disabled,
   insideGrid = false
}) => {
   const { getFieldDecorator } = form;
   const intl = useIntl();
   const { changeFieldPendingStatus, fieldsInfo, setFieldsInfo } = useFormContext();
   const sigCanvas = useRef<null | SignatureCanvas>(null);
   const containerRef = useRef<HTMLDivElement>(null);
   const [imgUrl, setImageUrl] = useState<string | null>(null);
   const [canvasWidth, setCanvasWidth] = useState<number>(0);
   const [infoText, setInfoText] = useState<any>(null);

   const infoClass =
      fieldsInfo[element.tech_name] && fieldsInfo[element.tech_name] !== ""
         ? "signature-field-info"
         : "";

   const clearCanvas = () => {
      sigCanvas.current?.clear();
      form.setFieldsValue({ [element.tech_name]: "" });
   };

   // function to convert the signature to one constant size
   // it works like this: get canvas created by user -> create new one, but sized as we want to -> put old canvas data (user signature) to new one
   const changeCanvasSize = (canvas: HTMLCanvasElement): HTMLCanvasElement => {
      const resizedCanvas = document.createElement("canvas");
      resizedCanvas.width = 300;
      resizedCanvas.height = 100;

      const context = resizedCanvas.getContext("2d");
      if (context) {
         context.drawImage(canvas, 0, 0, resizedCanvas.width, resizedCanvas.height);
      }

      return resizedCanvas;
   };

   const handleSave = () => {
      if (!sigCanvas.current.isEmpty()) {
         try {
            Message.loading(intl.formatMessage({ id: "global.loading" }));
            changeCanvasSize(sigCanvas.current.getCanvas()).toBlob(async (blob: Blob | null) => {
               if (blob) {
                  const formData = new FormData();
                  formData.append("multipartFile", blob, "signature.png");
                  const res = await uploadFieldFile(
                     formData,
                     `${hostCloud}/legacy/file/form?formFieldId=${element.object.UIP_ID}`
                  );
                  form.setFieldsValue({ [element.tech_name]: res.data.uploadFile.dtbId });
                  Message.destroy();
                  Message.success(intl.formatMessage({ id: "global.success" }));
               }
            });
         } catch (error) {
            console.error(error);
            Message.destroy();
            Message.error(intl.formatMessage({ id: "global.error" }));
         }
      }
   };

   useEffect(() => {
      const getDefaultFile = async () => {
         try {
            changeFieldPendingStatus && changeFieldPendingStatus(true);
            const file = (await getBlob(element.value)).data.default;
            setImageUrl(file);
         } catch {
            Message.error(intl.formatMessage({ id: "global.error" }));
         } finally {
            changeFieldPendingStatus && changeFieldPendingStatus(false);
         }
      };

      if (element.value && disabled) {
         getDefaultFile();
      } else if (containerRef.current && sigCanvas.current) {
         // they want this field to be bigger on bigger screens, so we are adding container ref + 1/3 of container ref
         setCanvasWidth(
            window?.innerWidth <= 1050
               ? containerRef.current.offsetWidth
               : containerRef.current.offsetWidth + containerRef.current.offsetWidth / 3
         );
      }
   }, []);

   // resizing on page size change
   useEffect(() => {
      const updateCanvasWidth = () => {
         if (containerRef.current) {
            // they want this field to be bigger on bigger screens, so we are adding container ref + 1/3 of container ref
            const newWidth =
               window?.innerWidth <= 1050
                  ? containerRef.current.offsetWidth
                  : containerRef.current.offsetWidth + containerRef.current.offsetWidth / 3;
            setCanvasWidth(newWidth);
         }
      };

      updateCanvasWidth();
      window.addEventListener("resize", updateCanvasWidth);

      return () => {
         window.removeEventListener("resize", updateCanvasWidth);
      };
   }, [element]);

   useEffect(() => {
      const infoText = fieldsInfo[element.tech_name];
      setInfoText(infoText);
      handleTemplateAlert(element.tech_name, infoText);
   }, [fieldsInfo[element.tech_name]]);

   // The canvas is shown when the field is not disabled, otherwise the image is shown
   return (
      <Form.Item
         key={key}
         label={!insideGrid && element.label}
         className={`signature-field ${infoClass} has-alert-${element.hasAlert} _type_${element.type}_type_ field_${element.tech_name} _id_${element.id}_id_`}
         style={{
            display: isVisible(element) ? "" : "none",
            gridArea: handleGridTechname(element.tech_name)
         }}
      >
         <div ref={containerRef}>
            {getFieldDecorator(element.tech_name, {
               rules: [
                  {
                     required: isRequired(element),
                     message: intl.formatMessage({ id: "field.required" })
                  }
               ],
               initialValue: element.value
            })(
               <>
                  {disabled ? (
                     imgUrl ? (
                        <img src={imgUrl || ""} alt="signature" className="signature-image" />
                     ) : (
                        <Spin />
                     )
                  ) : (
                     <div className="signature-canvas-container">
                        <SignatureCanvas
                           ref={sigCanvas}
                           canvasProps={{
                              width: canvasWidth,
                              height: 200,
                              className: "signature-canvas"
                           }}
                        />
                        <div className="signature-buttons">
                           <Button onClick={clearCanvas} className="signature-button">
                              {intl.formatMessage({ id: "forms.clear" })}
                           </Button>
                           <Button onClick={handleSave} className="signature-button">
                              {intl.formatMessage({ id: "forms.save" })}
                           </Button>
                        </div>
                     </div>
                  )}
               </>
            )}
            {renderDevInfo(element)}
            <FieldInfo text={infoText} />
         </div>
      </Form.Item>
   );
};

export default SignatureField;
